<template>
  <div class="project-grid" :class="['project-grid', theme, { 'affiche-mode': afficheMode, 'cv-mode': cvMode }]">
    <!-- Filter buttons -->
    <div class="filter-buttons" 
      :class="{ 'light-theme': isLightTheme, 'dark-theme': !isLightTheme }"
      v-if="!selectedProject">
      <button
        v-for="option in formatOptions"
        :key="option.value"
        @click="filterProjects(option.value)"
        :class="{ active: activeFilter === option.value }"
      >
        {{ option.text }}
      </button>
    </div>

  <div 
    class="secondary-filters" 
    :class="{ 'light-theme': isLightTheme, 'dark-theme': !isLightTheme }"
    v-if="(activeFilter === 'fiction' || activeFilter === 'doc') && !selectedProject"
  >
      <button
        @click="filterProjects(activeFilter, 'short')"
        :class="{ active: secondaryFilter === 'short' }"
      >
        Courts-métrages
      </button>
      <button
        @click="filterProjects(activeFilter, 'feature')"
        :class="{ active: secondaryFilter === 'feature' }"
      >
        Longs-métrages
      </button>
            <button
        @click="filterProjects(activeFilter, 'serie')"
        :class="{ active: secondaryFilter === 'serie' }"
      >
        Séries
      </button>
    </div>

    <div ref="gridTopMarker"></div>
    <!-- Project grid -->


    <div class="grid-container" :class="{ transitioning: isTransitioning }" v-if="!selectedProject && !cvMode">
      <!-- MODE NORMAL (images + clics) -->
      <div
        class="project-card"
        :class="{ 'light-theme': isLightTheme, 'dark-theme': !isLightTheme }"
        v-for="project in filteredProjects"
        :key="project.id + '-' + (afficheMode ? 'affiche' : 'vignette')"
        @click="selectProject(project)"
      >
        <div class="image-card" :class="getRatioClass(project, afficheMode)">
          <div class="image-container" v-if="!project.imageError">
            <img
              :src="getProjectImage(project['name-id'])"
              alt="Image du projet"
              class="project-image"
              loading="lazy"
              @error="project.imageError = true"
              @load="(e) => e.target.classList.remove('loading')"
              @loadstart="(e) => e.target.classList.add('loading')"
            />
          </div>

          <!-- Fallback quand pas d’image -->
          <div class="image-fallback" v-else>
            <template v-if="afficheMode">
              <!-- Vignette en fond -->
              <div class="fallback-wrapper">
                <img
                  :src="`/images/vignettes/${project['name-id']}_VIGN.webp`"
                  alt="Vignette du projet"
                  class="project-image fallback-vignette"
                  loading="lazy"
                  @error="project.imageError = true"
                />

                <!-- Texte superposé -->
                <div class="fallback-overlay">
                  <h2 v-html="formatTitle(project.projet)"></h2>
                  <p>{{ project.date }}</p>
                  <p>
                    {{ project.format.includes('serie') ? 'Réalisée par' : 'Réalisé par' }}
                    <span>{{ project.realisat }}</span>
                  </p>
                </div>
              </div>
            </template>

            <template v-else>
              <!-- Fallback texte normal pour le mode vignette -->
              <h2 v-html="formatTitle(project.projet)"></h2>
              <p>{{ project.date }}</p>
              <p>
                {{ project.format.includes('serie') ? 'Réalisée par' : 'Réalisé par' }}
                <span>{{ project.realisat }}</span>
              </p>
            </template>
          </div>
        </div>  

        <div class="card-detail" v-if="!afficheMode">
          <div class="flex-row space-between">
            <h2 v-html="formatTitle(project.projet)"></h2>
            <div class="flex-row">
              <hr class="solid">
              <div class="flex-col formats">
                <span class="self-end">{{ project.format.includes('music') ? 'musique' : '' }}</span>
                <span class="self-end">{{ project.format.includes('art') ? 'art' : '' }}</span>
                <span class="self-end">{{ project.format.includes('short') ? 'court-métrage' : '' }}</span>
                <span class="self-end">{{ project.format.includes('feature') ? 'long-métrage' : '' }}</span>
                <span class="self-end">{{ project.format.includes('serie') ? 'série' : '' }}</span>
                <span class="self-end">{{ project.format.includes('fiction') ? 'fiction' : '' }}</span>
                <span class="self-end">{{ project.format.includes('doc') ? 'documentaire' : '' }}</span>
              </div>
            </div>
          </div>
          <div><hr class="solid"></div>
          <div class="flex-row space-between">
            <div class="date"><span>{{ project.date }}</span></div>
            <div>
              <h3>{{ project.format.includes('serie') ? 'Réalisée par' : 'Réalisé par' }} <span>{{ project.realisat }}</span></h3>
            </div>
          </div>
        </div>
      </div>

    </div>

    <!-- MODE CV (texte uniquement, en liste) -->
    <div v-if="!selectedProject && cvMode" class="cv-list">
      <div
        class="cv-item"
        v-for="project in filteredProjects"
        :key="project.id"
      >
        <!-- Première ligne : projet / date / formats -->
        <div class="cv-line cv-line-top flex-row">
          <h2 v-html="formatTitle(project.projet)"></h2>
          <span class="date">{{ project.date }}</span>
          <span class="separator"></span>
          <div class="flex-row formats">
            <span v-if="project.format.includes('music')">musique</span>
            <span v-if="project.format.includes('art')">art</span>
            <span v-if="project.format.includes('short')">court-métrage</span>
            <span v-if="project.format.includes('feature')">long-métrage</span>
            <span v-if="project.format.includes('serie')">série</span>
            <span v-if="project.format.includes('fiction')">fiction</span>
            <span v-if="project.format.includes('doc')">documentaire</span>
          </div>
        </div>

        <!-- Seconde ligne : réalisateur / production -->
        <div class="cv-line cv-line-bottom flex-row">
          <div class="realisateur">
            <span>{{ project.format.includes('serie') ? 'Réalisée par ' : 'Réalisé par ' }}</span>
            <strong>{{ project.realisat }}</strong>
          </div>
          <div class="production" v-if="project.prod || project.prod2">
            <span class="separator"></span>
            <span>Produit par  </span>
            <strong v-if="project.prod">{{ project.prod }}</strong>
            <strong v-if="project.prod2">& {{ project.prod2 }}</strong>
          </div>
        </div>        

      </div>
    </div>


    <!-- Project details -->
    <ProjectDetail
      v-if="selectedProject"
      :project="selectedProject"
      :numberOfImages="numberOfImages.length"
      :closeProjectDetail="closeProjectDetail"
      :getCarouselImage="getCarouselImage"
      :isYouTubeVideo="isYouTubeVideo"
      :isVimeoVideo="isVimeoVideo"
      :getYouTubeEmbedUrl="getYouTubeEmbedUrl"
      :getVimeoEmbedUrl="getVimeoEmbedUrl"
      :theme="theme"
    />

<!-- Bouton flottant de changement de mode -->
<div v-if="!selectedProject" class="floating-buttons">

  <button class="toggle-mode-btn" @click="cvMode = !cvMode">
    {{ cvMode ? 'Visuel' : 'CV' }}
  </button>

  <button v-if="!cvMode" class="toggle-mode-btn" @click="afficheMode = !afficheMode">
    {{ afficheMode ? 'Stills' : 'Poster' }}
  </button>
</div>

  </div>
  
</template>

<script>
import { ref, onMounted, onBeforeUnmount, nextTick, computed, watch, watchEffect } from "vue";
import { useRoute } from "vue-router";
import ProjectDetail from "./ProjectDetail.vue";
import "./ProjectGrid.css"; 

export default {
  components: { ProjectDetail },

  props: {
    theme: String,
  },

  setup() {
    /* -----------------------------------------
     * STATE
     * ----------------------------------------- */
    const projects = ref([]);
    const selectedProject = ref(null);

    const activeFilter = ref(null);
    const secondaryFilter = ref(null);

    const afficheMode = ref(false);
    const cvMode = ref(false);

    const numberOfImages = ref([]);

    const isTransitioning = ref(false);

    const route = useRoute();

    /* -----------------------------------------
     * FORMAT OPTIONS
     * ----------------------------------------- */
    const formatOptions = [
      { value: null, text: "Tous" },
      { value: "fiction", text: "Fiction" },
      { value: "doc", text: "Documentaire" },
      { value: "music", text: "Musique" },
      { value: "art", text: "Art" },
    ];

    /* -----------------------------------------
     * FETCH PROJECTS
     * ----------------------------------------- */
    const fetchProjects = async () => {
      // Récupère le cache et le timestamp
      const cachedData = localStorage.getItem("projects");
      const cachedTimestamp = localStorage.getItem("projects_timestamp");

      // Vérifie si le cache existe et n'est pas expiré (24h = 86400000 ms)
      const isCacheValid = cachedData && cachedTimestamp &&
                          (Date.now() - parseInt(cachedTimestamp) < 86400000);

      if (isCacheValid) {
        projects.value = JSON.parse(cachedData);
        return; // Utilise le cache
      }

      // Sinon, fais la requête API
      try {
        const res = await fetch("https://julian-nouveau.com/api.php");
        const data = await res.json();
        projects.value = data.sort((a, b) => b.sortOrder - a.sortOrder);

        // Stocke les données et le timestamp
        localStorage.setItem("projects", JSON.stringify(projects.value));
        localStorage.setItem("projects_timestamp", Date.now().toString());
      } catch (error) {
        console.error("Erreur lors de la récupération des projets :", error);
        // En cas d'erreur, utilise le cache même s'il est expiré (fallback)
        if (cachedData) {
          projects.value = JSON.parse(cachedData);
        }
      }
    };
    /* -----------------------------------------
     * 🔥 COMPUTED CENTRAL : filteredProjects
     * ----------------------------------------- */
    const filteredProjects = computed(() => {
      if (!projects.value.length) return [];

      let list = [...projects.value];

      // Mode CV → afficher tous les projets
      if (!cvMode.value) {
        list = list.filter((p) => Number(p.cvOnly) === 0);
      }

      // Filtre principal
      if (activeFilter.value) {
        list = list.filter((p) =>
          p.format.split(",").includes(activeFilter.value)
        );
      }

      // Filtre secondaire
      if (secondaryFilter.value) {
        list = list.filter((p) =>
          p.format.split(",").includes(secondaryFilter.value)
        );
      }

      return list.sort((a, b) => b.sortOrder - a.sortOrder);
    });

    /* -----------------------------------------
     * FILTRAGE : simple et propre
     * ----------------------------------------- */
    const filterProjects = (primary, secondary = null) => {
      if (activeFilter.value !== primary) {
        activeFilter.value = primary;
        secondaryFilter.value = null;
        return;
      }

      if (secondary) {
        secondaryFilter.value =
          secondaryFilter.value === secondary ? null : secondary;
      }
    };

    /* -----------------------------------------
     * IMAGE HELPERS
     * ----------------------------------------- */
    const getProjectImage = (nameId) =>
      afficheMode.value
        ? `/images/affiches/${nameId}_AFFICHE.webp`
        : `/images/vignettes/${nameId}_VIGN.webp`;

    const getCarouselImage = (nameId, index) => {
      const base = `/images/CAROUSEL/${nameId}/${nameId}_${index}`;

      return {
        src: `${base}-1200.webp`, // image par défaut
        srcset: `
          ${base}-800.webp 800w,
          ${base}-1200.webp 1200w,
          ${base}-1800.webp 1800w
        `
      };
    };

    /* -----------------------------------------
     * RATIO
     * ----------------------------------------- */
    const getRatioClass = (project) =>
      afficheMode.value
        ? "ratio-2-3"
        : `ratio-${project.ratio || "16-9"}`;

    /* -----------------------------------------
     * TITRE : gestion des |
     * ----------------------------------------- */
    const formatTitle = (title) =>
      cvMode.value
        ? title.replace(/\|/g, " ")
        : title.replace(/\|/g, "<br>");

    /* -----------------------------------------
     * PROJECT DETAIL + URL HASH
     * ----------------------------------------- */
    const slugify = (text) =>
      text
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");

    const showProject = (project) => {
      selectedProject.value = project;
      numberOfImages.value = Array.from({ length: project.stills }, (_, i) => i + 1);
    };

    const selectProject = (project) => {
      showProject(project);
      const slug = slugify(project.projet);
      history.pushState({ projectSlug: slug }, "", `#${slug}`);
      window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const closeProjectDetail = () => {
      history.back();
    };

    /* -----------------------------------------
     * YOUTUBE / VIMEO
     * ----------------------------------------- */
    const isYouTubeVideo = (url) =>
      url.includes("youtube.com") || url.includes("youtu.be");

    const isVimeoVideo = (url) => url.includes("vimeo.com");

    const getYouTubeEmbedUrl = (url) => {
      const id =
        url.split("v=")[1]?.split("&")[0] ||
        url.split("/").pop();
      return `https://www.youtube.com/embed/${id}`;
    };

    const getVimeoEmbedUrl = (url) => {
      const id = url.split("/").pop();
      return `https://player.vimeo.com/video/${id}`;
    };

    /* -----------------------------------------
     * MOUNT
     * ----------------------------------------- */
    const resetToHome = () => {
      selectedProject.value = null;

      // enlève le #slug de l'URL sans ajouter d'entrée d'historique
      if (window.location.hash) {
        history.replaceState(null, "", window.location.pathname + window.location.search);
      }

      window.scrollTo({ top: 0, behavior: "smooth" });
    };


    onMounted(async () => {
      window.addEventListener("reset-to-etalonnage", resetToHome);
      afficheMode.value = localStorage.getItem("afficheMode") === "true";
      cvMode.value = localStorage.getItem("cvMode") === "true";

      await fetchProjects();

      // Ouvre projet si #slug présent
      watchEffect(() => {
        if (!projects.value.length) return;
        if (!window.location.hash) return;

        const slug = window.location.hash.substring(1);
        const proj = projects.value.find(
          (p) => slugify(p.projet) === slug
        );
        if (proj) {
          selectedProject.value = proj;
          numberOfImages.value = Array.from(
            { length: proj.stills },
            (_, i) => i + 1
          );
        }
      });

      // Retour arrière navigateur
    window.addEventListener("popstate", (e) => {
      if (e.state?.lightboxOpen) return; // la lightbox gère ça elle-même

      if (e.state?.projectSlug) {
        const proj = projects.value.find(
          (p) => slugify(p.projet) === e.state.projectSlug
        );
        if (proj) showProject(proj); // pas de pushState ici
      } else {
        selectedProject.value = null;
      }
    });
    });

    onBeforeUnmount(() => {
      window.removeEventListener("reset-to-etalonnage", resetToHome);
    });

    /* -----------------------------------------
     * WATCHES (storage + transition affiches)
     * ----------------------------------------- */
    watch(afficheMode, async (v) => {
      localStorage.setItem("afficheMode", v);
      isTransitioning.value = true;
      await nextTick();
      requestAnimationFrame(() => {
        projects.value.forEach((p) => (p.imageError = false));
        setTimeout(() => (isTransitioning.value = false), 150);
      });
    });

    watch(cvMode, (v) => {
      localStorage.setItem("cvMode", v);
    });

    /* -----------------------------------------
     * RETURN
     * ----------------------------------------- */
    return {
      projects,
      filteredProjects,
      formatOptions,
      activeFilter,
      secondaryFilter,
      afficheMode,
      cvMode,
      selectedProject,
      numberOfImages,
      isTransitioning,

      filterProjects,
      selectProject,
      closeProjectDetail,

      getProjectImage,
      getCarouselImage,
      getRatioClass,
      formatTitle,

      isYouTubeVideo,
      isVimeoVideo,
      getYouTubeEmbedUrl,
      getVimeoEmbedUrl,
    };
  },

  computed: {
    isLightTheme() {
      return this.theme === "light";
    },
  },
};
</script>


<style>
</style>
